import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useGetSuppliers } from '../hooks/useGetSuppliers';
import type { UserOrganisation } from '../types/types';
import {
  Table,
  Form,
  Button,
  Modal,
  Row,
  Col,
  Pagination,
  Badge,
  Container,
  Spinner,
} from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { useDeleteSuppliers } from '../hooks/useDeleteSupplier';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCountryNameFromIso } from '../util/countryUtil';

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export default function SupplierList({ reloadKey }: { reloadKey: number }) {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQueryParams();

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected
  ) as UserOrganisation | undefined;

  const [search, setSearch] = useState(query.get('search') || '');
  const [page, setPage] = useState(Number(query.get('page') || 1));
  const [sortBy, setSortBy] = useState<
    'company_name' | 'registration_number' | 'updated_at'
  >(
    (query.get('sortBy') as
      | 'company_name'
      | 'registration_number'
      | 'updated_at') || 'updated_at'
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    (query.get('sortDirection') as 'asc' | 'desc') || 'desc'
  );

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [localReloadKey, setLocalReloadKey] = useState(0);

  const pageSize = 10;

  const { deleteSuppliers, loading: deleting } = useDeleteSuppliers();

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (page > 1) params.set('page', String(page));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDirection) params.set('sortDirection', sortDirection);

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true }
    );
  }, [search, page, sortBy, sortDirection]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      if (search) {
        setPage(1);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [search]);

  const effectiveReloadKey = reloadKey + localReloadKey;

  const { suppliers, total, loading, error } = useGetSuppliers(
    selectedOrganisation?.organisation?.id ?? '',
    {
      page,
      pageSize,
      search: debouncedSearch,
      sortBy,
      sortDirection,
    },
    effectiveReloadKey
  );

  if (!selectedOrganisation?.organisation) {
    return <div>Please select an organisation to see suppliers.</div>;
  }

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const confirmDelete = async () => {
    const success = await deleteSuppliers(selectedIds, {
      deleteContacts: true, // always delete contacts
    });
    if (success) {
      setShowDeleteModal(false);
      setSelectedIds([]);
      setLocalReloadKey((prev) => prev + 1);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const handleSort = (
    column: 'company_name' | 'registration_number' | 'updated_at'
  ) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (
    column: 'company_name' | 'registration_number' | 'updated_at'
  ) => {
    if (sortBy !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div>
      <Row>
        <Col>
          {selectedIds.length > 0 && (
            <div className="mb-2">
              <Button
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
                disabled={deleting}
              >
                Delete ({selectedIds.length})
              </Button>
            </div>
          )}
        </Col>
        <Col className="justify-content-end d-flex">
          <Form.Group className="mb-3" controlId="supplierSearch">
            <Form.Control
              type="text"
              placeholder="Search suppliers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>
      {loading && (
        <Container fluid className="d-flex">
          <Row className="justify-content-center align-self-center w-100">
            <Col className="text-center">
              <Spinner animation="border" role="status" variant="primary" />
            </Col>
          </Row>
        </Container>
      )}
      {error && <div>Error loading suppliers: {error}</div>}
      {!loading && suppliers.length === 0 && <div>No suppliers found.</div>}

      {suppliers.length > 0 && (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={
                      selectedIds.length === suppliers.length &&
                      suppliers.length > 0
                    }
                    onChange={(e) =>
                      setSelectedIds(
                        e.target.checked
                          ? suppliers
                              .filter((s) => s !== null && s!.id !== undefined)
                              .map((s) => s!.id as string)
                          : []
                      )
                    }
                  />
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('company_name')}
                >
                  Supplier {renderSortIcon('company_name')}
                </th>
                <th>Flags</th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('registration_number')}
                >
                  Registration nr. {renderSortIcon('registration_number')}
                </th>
                <th>Country</th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('updated_at')}
                >
                  Updated {renderSortIcon('updated_at')}
                </th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((supplier) =>
                supplier ? (
                  <tr
                    key={supplier.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      navigate(`/suppliers/${supplier.id}`, {
                        state: { from: location },
                      })
                    }
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.includes(supplier.id ?? '')}
                        onChange={() => toggleSelection(supplier.id ?? '')}
                      />
                    </td>
                    <td>{supplier.company_name}</td>
                    <td>
                      {supplier.flags.map((flag) => (
                        <Badge
                          bg={
                            flag === 'duplicate'
                              ? 'warning'
                              : flag === 'incomplete'
                              ? 'danger'
                              : flag === 'review'
                              ? 'info'
                              : flag === 'imported'
                              ? 'primary'
                              : 'light'
                          }
                          key={flag}
                          className="me-1"
                        >
                          {flag}
                        </Badge>
                      ))}
                    </td>
                    <td>{supplier.registration_number}</td>
                    <td>{getCountryNameFromIso(supplier.country) || '-'}</td>
                    <td>
                      {dayjs(supplier.updated_at).format('DD.MM.YYYY HH:mm')}
                    </td>
                  </tr>
                ) : null
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.First
                disabled={page === 1}
                onClick={() => setPage(1)}
              />
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              />
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === page}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              />
              <Pagination.Last
                disabled={page === totalPages}
                onClick={() => setPage(totalPages)}
              />
            </Pagination>
          )}
        </>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete {selectedIds.length} supplier
            {selectedIds.length > 1 ? 's' : ''} and all of their assessments?
          </p>
          <p className="text-danger">You cannot undo this operation</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
